// BASEMENT.exe — the secret application at the end of the ARG's first level. Appears on the desktop
// only after the key turns on the internal web. Prints a certificate you can save and post.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { AddressBar, Counter, Field, Marquee, Mast, SERIF, StatusBar, WEB, webCss, xpCss } from 'lib/xp';
import { unlockedAt } from './state';

const CA = '0x88888cC1c17FfcBa563F418c61596b3fa2Ca2e2c';
const SITE = 'internetcoin.vip';

function guessNick() {
  for (const k of ['ic_messenger_nick', 'ic:nick']) {
    try {
      const v = localStorage.getItem(k);
      if (v && v.length < 24) return v.replace(/^"|"$/g, '');
    } catch (e) { /* private mode */ }
  }
  return 'fren';
}

const ASCII = [
  ' ______  __  __  ______       ______  ______  ______  ______  __    __  ______  __   __  ______',
  '/\\__  _\\/\\ \\_\\ \\/\\  ___\\     /\\  == \\/\\  __ \\/\\  ___\\/\\  ___\\/\\ "-./  \\/\\  ___\\/\\ "-.\\ \\/\\__  _\\',
  '\\/_/\\ \\/\\ \\  __ \\ \\  __\\     \\ \\  __<\\ \\  __ \\ \\___  \\ \\  __\\\\ \\ \\-./\\ \\ \\  __\\\\ \\ \\-.  \\/_/\\ \\/',
  '   \\ \\_\\ \\ \\_\\ \\_\\ \\_____\\    \\ \\_____\\ \\_\\ \\_\\/\\_____\\ \\_____\\ \\_\\ \\ \\_\\ \\_____\\ \\_\\\\"\\_\\  \\ \\_\\',
  '    \\/_/  \\/_/\\/_/\\/_____/     \\/_____/\\/_/\\/_/\\/_____/\\/_____/\\/_/  \\/_/\\/_____/\\/_/ \\/_/   \\/_/',
];

// A tiny local rounded-rect path (rather than relying on browser support for ctx.roundRect).
function roundedRect(g, x, y, w, h, r) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

function Basement() {
  const [name, setName] = useState(guessNick);
  const [dataUrl, setDataUrl] = useState(null);
  const canvasRef = useRef(null);
  const at = unlockedAt();
  const when = at ? new Date(at) : new Date();
  const dateStr = when.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const render = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return null;
    c.width = 1200;
    c.height = 675;
    const g = c.getContext('2d');

    // paper
    g.fillStyle = '#f5f4ea';
    g.fillRect(0, 0, 1200, 675);

    // outer + inner frame
    g.strokeStyle = '#716f64';
    g.lineWidth = 3;
    g.strokeRect(28, 28, 1144, 619);
    g.strokeStyle = '#aca899';
    g.lineWidth = 1;
    g.strokeRect(40, 40, 1120, 595);

    g.textAlign = 'center';

    // navy title band, flush with the top of the inner frame
    g.fillStyle = '#0054e3';
    g.fillRect(40, 40, 1120, 70);
    g.fillStyle = '#ffffff';
    g.font = 'bold 48px "Trebuchet MS", Tahoma, sans-serif';
    g.fillText('CERTIFICATE OF DESCENT', 600, 90);

    g.fillStyle = '#6d6a5f';
    g.font = '18px "Trebuchet MS", Tahoma, sans-serif';
    g.fillText('T H E   I N T E R N E T   ·   L E V E L   1', 600, 140);

    g.fillStyle = '#3b3b3b';
    g.font = '24px "Trebuchet MS", Tahoma, sans-serif';
    g.fillText('this certifies that', 600, 195);

    g.fillStyle = '#000000';
    g.font = 'bold 60px "Trebuchet MS", Tahoma, sans-serif';
    g.fillText((name || 'fren').slice(0, 24), 600, 270);

    g.fillStyle = '#3b3b3b';
    g.font = '26px "Trebuchet MS", Tahoma, sans-serif';
    g.fillText('found the basement of the internet', 600, 330);
    g.fillText(`on ${dateStr}`, 600, 368);

    g.fillStyle = '#6d6a5f';
    g.font = '20px "Lucida Console", monospace';
    g.fillText('found the folder. found the door. turned the key. (how? ask the lobby.)', 600, 430);

    // key / CA line, on an LED plate — the one lime-on-dark element on the certificate
    const ledText = `key accepted · level 1 of ? · ${CA}`;
    g.font = '16px "Lucida Console", monospace';
    const ledW = g.measureText(ledText).width + 40;
    const ledH = 40;
    const ledX = 600 - ledW / 2;
    const ledY = 480;
    roundedRect(g, ledX, ledY, ledW, ledH, 6);
    g.fillStyle = '#0b0f04';
    g.fill();
    g.fillStyle = '#ccff00';
    g.fillText(ledText, 600, ledY + ledH / 2 + 5);

    g.fillStyle = '#0054e3';
    g.font = 'bold 22px "Trebuchet MS", Tahoma, sans-serif';
    g.fillText(`$INTERNET · ${SITE}`, 600, 580);

    return c.toDataURL('image/png');
  }, [name, dateStr]);

  useEffect(() => {
    setDataUrl(render());
  }, [render]);

  function save() {
    const url = render();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `basement-${(name || 'fren').replace(/[^a-z0-9]+/gi, '_')}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  const share = encodeURIComponent(`i found the basement of the internet.\n\nyou haven't.\n\n${SITE}\n\n$internet #internetcoin`);

  return (
    <Div>
      <div className="xp__menubar">
        <button type="button">File</button>
        <button type="button">Edit</button>
        <button type="button">Help</button>
        <span className="xp__menubar__brand">Level 1 of ?</span>
      </div>
      <AddressBar path="basement.internetcoin.vip/level1.htm" />
      <div className="xp__client web web--brown">
        <Mast
          kicker="level 1 of ?"
          title="THE BASEMENT"
          right={(
            <>
              <span className="web__sticker web__sticker--blink">SECRET</span>
              <span className="bs__visitor">you are visitor № <Counter value={1} digits={7} /></span>
            </>
          )}
        />
        <Marquee>★ you found it ★ don&apos;t tell the others how you got here ★ there is a level 2 ★ it is not built yet ★ watch the lobby ★</Marquee>
        <div className="web__body">
          <div className="web__led bs__asciiPlate">
            <pre className="bs__ascii">{ASCII.join('\n')}</pre>
          </div>
          <div className="web__panel web__panel--brown">
            <div className="web__panel__t">Level 1 · complete</div>
            <div className="bs__line bs__line--big">you found it.</div>
            <div className="bs__line">level 1 of ?. the lobby dropped a clue, the disk had a folder that shouldn&apos;t exist, the door was on the internal web, and the key was in the contract address the whole time.</div>
          </div>
          <div className="web__panel web__panel--white bs__cert">
            {dataUrl && <img className="xp__card" src={dataUrl} alt="certificate of descent" />}
          </div>
          <div className="web__panel web__panel--navy bs__row">
            <div className="web__panel__t">Certificate</div>
            <label>Name on the certificate</label>
            <Field value={name} onChange={e => setName(e.target.value.slice(0, 24))} spellCheck={false} />
            <button type="button" className="web__btn" onClick={save}>Save certificate (.png)</button>
            <a className="web__btn web__btn--blue bs__x" href={`https://twitter.com/intent/tweet?text=${share}`} target="_blank" rel="noreferrer">Post on X</a>
          </div>
          <div className="web__note bs__line--dim">there is a level 2. it is not built yet. watch the lobby. don&apos;t tell the others how you got here. (they&apos;ll ask.)</div>
          <div className="web__hr" />
          <div className="web__foot">best viewed at 800×600 · level 1 of ? · © 2001 the internet</div>
        </div>
      </div>
      <StatusBar>
        <span>key accepted</span>
        <span>level 1 of ?</span>
        <span>Internet</span>
      </StatusBar>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Div>
  );
}

const Div = styled.div`
  ${xpCss};
  ${webCss};
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .xp__client {
    flex: 1;
    overflow: auto;
    padding: 0;
  }

  /* the ASCII banner — the module's own lime-on-navy readable plate */
  .bs__asciiPlate {
    display: block;
    width: 100%;
    box-sizing: border-box;
    margin: 0 0 14px;
  }
  .bs__ascii {
    margin: 0;
    font: 9px/10px 'Lucida Console', Consolas, monospace;
    white-space: pre;
    overflow-x: auto;
  }

  /* the visitor line in the masthead — light text, it sits on the dark band */
  .bs__visitor {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    color: #fff3dc;
  }

  .bs__line {
    line-height: 1.5;
    margin-bottom: 8px;
    max-width: 760px;
    font-size: 12px;
    color: ${WEB.ink};
  }
  .bs__line--big {
    font-size: 16px;
    font-weight: bold;
    color: #000;
    font-family: ${SERIF};
  }
  .bs__line--dim {
    font-style: italic;
  }

  .bs__cert img {
    display: block;
    width: 640px;
    max-width: 100%;
    box-sizing: border-box;
    margin: 0 auto;
  }

  .bs__row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .bs__row .web__panel__t {
    flex: 0 0 100%;
  }
  .bs__x {
    display: inline-flex;
    align-items: center;
  }
`;

export default Basement;
